import { Minus, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaShoppingCart } from "react-icons/fa";
import { useCart } from "context/CartContext";
import { useTranslation } from "context/LanguageContext";
import { useHome } from "context/HomeContext";
import {
  DEFAULT_CHECKOUT_ORDER_DEFAULTS,
  getCurrentClientConfig,
} from "config/clientConfig";
import {
  normalizePhoneExtension,
  submitCheckout,
  validateCheckoutCustomer,
} from "services/checkoutService";
import { saveTrackedOrder } from "services/orderTrackingStorage";
import villages from "data/villages.json";

export default function CartSidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const {
    cart,
    getCartCount,
    updateQuantity,
    removeFromCart,
    getCartTotal,
    clearCart,
  } = useCart();
  const { t, language } = useTranslation();
  const { deliveryCosts } = useHome();
  const clientConfig = getCurrentClientConfig();
  const checkoutConfig = clientConfig?.checkout || {};
  const defaultPhoneExtension =
    normalizePhoneExtension(checkoutConfig?.defaults?.phoneExtensionDefault) ||
    DEFAULT_CHECKOUT_ORDER_DEFAULTS.phoneExtensionDefault;

  const buildInitialCustomerForm = () => ({
    customerName: "",
    customerPhone: "",
    customerPhoneExtension: normalizePhoneExtension(defaultPhoneExtension),
    address: "",
    villageId: "",
    areaId: "",
    notes: "",
  });

  const [customerForm, setCustomerForm] = useState(buildInitialCustomerForm);
  const [customerErrors, setCustomerErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [villageQuery, setVillageQuery] = useState("");
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [deliveryCost, setDeliveryCost] = useState(0);

  const isUltraLinked = Boolean(clientConfig?.linkedWithUltra);

  const filteredVillages =
    !selectedVillage && villageQuery.trim()
      ? villages
          .filter((village) =>
            (village?.village_name || "")
              .toLowerCase()
              .includes(villageQuery.trim().toLowerCase())
          )
          .slice(0, 30)
      : [];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setCustomerForm((prev) => ({
      ...prev,
      customerPhoneExtension: prev.customerPhoneExtension || defaultPhoneExtension,
    }));
  }, [defaultPhoneExtension, isOpen]);

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);

  const handleFieldChange = (field, value) => {
    const nextValue =
      field === "customerPhoneExtension"
        ? normalizePhoneExtension(value)
        : value;

    setCustomerForm((prev) => ({
      ...prev,
      [field]: nextValue,
    }));

    if (customerErrors[field]) {
      setCustomerErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSelectVillage = (village) => {
    setSelectedVillage(village);
    setVillageQuery("");
    setCustomerForm((prev) => ({
      ...prev,
      address: village?.village_name || "",
      villageId: village?.id != null ? String(village.id) : "",
      areaId: village?.area_id != null ? String(village.area_id) : "",
    }));
    const areaCost = deliveryCosts[String(village?.area_id)];
    const cost =
      areaCost != null
        ? Number(areaCost)
        : Number(village?.delivery_cost) || 0;
    setDeliveryCost(Number.isFinite(cost) ? cost : 0);
    if (customerErrors.village) {
      setCustomerErrors((prev) => ({ ...prev, village: "" }));
    }
  };

  const handleClearVillage = () => {
    setSelectedVillage(null);
    setVillageQuery("");
    setDeliveryCost(0);
    setCustomerForm((prev) => ({
      ...prev,
      address: "",
      villageId: "",
      areaId: "",
    }));
  };

  const handleCheckout = async () => {
    const errors = validateCheckoutCustomer(customerForm);
    if (Object.keys(errors).length > 0) {
      setCustomerErrors(errors);
      return;
    }

    if (isUltraLinked && !customerForm.villageId) {
      setCustomerErrors((prev) => ({
        ...prev,
        village: t("selectVillage"),
      }));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitCheckout({
        cart,
        customer: customerForm,
        language,
        checkoutConfig,
        deliveryCost,
        linkedWithUltra: isUltraLinked,
      });

      if (result.provider === "api") {
        saveTrackedOrder(clientConfig.tenantId, {
          orderId: result.orderId,
          trackingToken: result.trackingToken,
          trackingTokenExpiresAt: result.trackingTokenExpiresAt,
        });
      }

      clearCart();
      setCustomerErrors({});
      setCustomerForm(buildInitialCustomerForm());
      onClose();
      if (result.provider === "api") {
        navigate(`/order-confirmation/${result.orderId}`);
      }
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || error?.response?.data?.error;
      alert(responseMessage || error?.message || "Failed to place order.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cartCount = getCartCount();

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      <div
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-lg transform transition-transform duration-300 flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">
            {t("shoppingCart")} ({cartCount})
          </h2>
          <button onClick={onClose}>
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="text-center text-gray-500 mt-10">
              <FaShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-3" />
              <p>{t("cartIsEmpty")}</p>
            </div>
          ) : (
            cart.map((item) => (
              <div
                key={`${item.id}-${item.selectedSize || ""}-${item.selectedColor || ""}`}
                className="flex gap-4 p-3 mb-3 bg-gray-50 rounded-lg border"
              >
                <div className="w-20 h-20 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                  {item.image ? (
                    <img
                      src={`${item.image}`}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm">{item.name}</h3>
                  {item.selectedSize && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t("size")}: {item.selectedSize}
                    </p>
                  )}
                  {item.selectedColor && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t("color")}: {item.selectedColor}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-blue-600">
                      {formatPrice(item.selling_price ?? item.price ?? 0)}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const currentQty =
                            item.cartQuantity ?? item.quantity ?? 0;
                          updateQuantity(
                            item.id,
                            currentQty - 1,
                            item.selectedSize
                          );
                        }}
                        className="p-1 border rounded hover:bg-gray-100"
                      >
                        <Minus className="w-3 h-3" />
                      </button>

                      <span className="w-6 text-center">
                        {item.cartQuantity ?? item.quantity ?? 0}
                      </span>

                      <button
                        onClick={() => {
                          const currentQty =
                            item.cartQuantity ?? item.quantity ?? 0;
                          updateQuantity(
                            item.id,
                            currentQty + 1,
                            item.selectedSize
                          );
                        }}
                        className="p-1 border rounded hover:bg-gray-100"
                      >
                        <Plus className="w-3 h-3" />
                      </button>

                      <button
                        onClick={() => removeFromCart(item.id, item.selectedSize)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {cart.length > 0 && (
          <div className="p-4 border-t flex-shrink-0">
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {t("customerName")} *
                </label>
                <input
                  type="text"
                  value={customerForm.customerName}
                  onChange={(e) =>
                    handleFieldChange("customerName", e.target.value)
                  }
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder={t("enterCustomerName")}
                />
                {customerErrors.customerName ? (
                  <p className="mt-1 text-xs text-red-600">
                    {customerErrors.customerName}
                  </p>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {t("customerPhone")} *
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customerForm.customerPhoneExtension}
                    onChange={(e) =>
                      handleFieldChange("customerPhoneExtension", e.target.value)
                    }
                    className="w-24 border rounded-lg px-3 py-2 text-sm"
                    placeholder="961"
                    maxLength={3}
                    inputMode="numeric"
                  />
                  <input
                    type="text"
                    value={customerForm.customerPhone}
                    onChange={(e) =>
                      handleFieldChange("customerPhone", e.target.value)
                    }
                    className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    placeholder={t("enterCustomerPhone")}
                  />
                </div>
                {customerErrors.customerPhone ? (
                  <p className="mt-1 text-xs text-red-600">
                    {customerErrors.customerPhone}
                  </p>
                ) : null}
                {customerErrors.customerPhoneExtension ? (
                  <p className="mt-1 text-xs text-red-600">
                    {customerErrors.customerPhoneExtension}
                  </p>
                ) : null}
              </div>

              {isUltraLinked && (
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {t("village")}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={
                      selectedVillage
                        ? selectedVillage.village_name
                        : villageQuery
                    }
                    onChange={(e) => {
                      if (selectedVillage) return;
                      setVillageQuery(e.target.value);
                      if (customerErrors.village) {
                        setCustomerErrors((prev) => ({
                          ...prev,
                          village: "",
                        }));
                      }
                    }}
                    readOnly={Boolean(selectedVillage)}
                    className="w-full border rounded-lg px-3 py-2 text-sm pr-8 disabled:bg-gray-100"
                    placeholder={t("searchVillage")}
                    disabled={Boolean(selectedVillage)}
                  />
                  {selectedVillage && (
                    <button
                      type="button"
                      onClick={handleClearVillage}
                      aria-label={t("clear")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {!selectedVillage && filteredVillages.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border rounded-lg shadow-lg">
                    {filteredVillages.map((village) => (
                      <li key={village.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectVillage(village)}
                          className="w-full text-right px-3 py-2 text-sm hover:bg-gray-100"
                        >
                          {village.village_name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {customerErrors.village ? (
                  <p className="mt-1 text-xs text-red-600">
                    {customerErrors.village}
                  </p>
                ) : null}
              </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {t("notes")}
                </label>
                <textarea
                  value={customerForm.notes}
                  onChange={(e) => handleFieldChange("notes", e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                  rows={2}
                  placeholder={t("optional")}
                />
              </div>
            </div>

            <div className="space-y-1 mb-3">
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t("subtotal")}:</span>
                <span>{formatPrice(getCartTotal())}</span>
              </div>
              {isUltraLinked && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t("delivery")}:</span>
                  <span>{formatPrice(deliveryCost)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-lg font-semibold">{t("total")}:</span>
                <span className="text-2xl font-bold text-blue-600">
                  {formatPrice(getCartTotal() + deliveryCost)}
                </span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t("submitting") : t("proceedtoCheckout")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
